import React, { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import MentorRouteGuard from "./components/auth/MentorRouteGuard";
import ProtectedRoute from "./pages/MentorProfile/ProtectedRoute";
import ProjectDetailsPage from "./pages/projectDetails";
import AuthProtectedRoute from "./components/auth/AuthProtectedRoute";

const App = lazy(() => import("./pages/App"));
const About = lazy(() => import("./pages/about"));
const FAQ = lazy(() => import("./pages/faq"));
const LiveQA = lazy(() => import("./pages/liveQA"));
const Courses = lazy(() => import("./pages/courses"));
const TermsAndCondition = lazy(() => import("./pages/terms"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));
const Contact = lazy(() => import("./pages/contact"));
const ContactPartner = lazy(() => import("./pages/contactPartner"));
const Testimonial = lazy(() => import("./pages/testimonial"));
const FreeCourse = lazy(() => import("./pages/freeCourse"));
const WaitList = lazy(() => import("./pages/waitList"));
const ReferAFriend = lazy(() => import("./pages/referAFriend"));
const AllCoursePage = lazy(() => import("./pages/allCoursePages"));
const RecommendedAllCoursePage = lazy(
  () => import("./pages/recommendedAllCoursePages"),
);
const FastTrack = lazy(() => import("./pages/fastTrack"));
const WorkSkillGaragePage = lazy(() => import("./pages/workSkillGarage"));
const SignUpPage = lazy(() => import("./pages/signUpPage"));
const EmailVerificationSuccess = lazy(
  () => import("./pages/emailVerificationSuccess"),
);
const ResetPasswordEmailSent = lazy(
  () => import("./pages/resetPasswordEmailSent"),
);
const NewPasswordPage = lazy(() => import("./pages/newPasswordPage"));
const ResendEmailPage = lazy(() => import("./pages/resendEmailPage"));
const VerifyEmailPage = lazy(() => import("./pages/verifyEmail"));
const ResetPasswordPage = lazy(() => import("./pages/resetPassword"));
const PasswordSuccessPage = lazy(() => import("./pages/passwordSuccessPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const VerificationCodePage = lazy(() => import("./pages/verificationCodePage"));
const ResetLinkPage = lazy(() => import("./pages/ResetLinkPage"));
const DashboardPage = lazy(() => import("./pages/dashboardPage"));
const Profile = lazy(() => import("./pages/profile"));
const BlogPage = lazy(() => import("./pages/blog"));
const BlogPost = lazy(() => import("./pages/blogPost"));
const AllMentorsPage = lazy(() => import("./pages/AllMentors"));
const BookingsPage = lazy(() => import("./pages/Bookings/Bookings"));
const PrivacyPolicyPage = lazy(() => import("./pages/privacy"));
const RefundPolicyPage = lazy(() => import("./pages/refund"));
const MentorDetails = lazy(() => import("./pages/MentorDetails"));
const VerificationExpired = lazy(() => import("./pages/VerificationExpired"));
const SuccessEmail = lazy(() => import("./pages/SuccessEmail"));
const MentorSignUpPage = lazy(() => import("./pages/mentorSignUpPage"));
const MentorVerifyEmailPage = lazy(
  () => import("./pages/mentorVerifyEmailPage"),
);
const MentorSessions = lazy(
  () => import("./pages/MentorProfile/mentorSessions"),
);
const MentorProfile = lazy(() => import("./pages/MentorProfile/mentorProfile"));
const BecomeAMentorPage = lazy(() => import("./pages/BecomeAMentor"));
const CookiePolicy = lazy(() => import("./pages/cookie"));
const CourseDetailsPage = lazy(() => import("./pages/courseDetails"));
const QuizPage = lazy(() => import("./pages/quiz"));
const QuizCompletedPage = lazy(() => import("./pages/completed"));
const WatchVideoPage = lazy(() => import("./pages/watchVideoPage"));
const CheckOutPage = lazy(() => import("./pages/checkOutPage"));
const SelfPacedCourse = lazy(() => import("./pages/selfPacedCourse"));
const CourseLandingPage = lazy(() => import("./pages/courseLanding"));
const ShortCoursePage = lazy(() => import("./pages/shortCourse"));
const SpeakToAdvisorPage = lazy(() => import("./pages/speakToAdvisorPage"));
const TechExperienceCoursePage = lazy(
  () => import("./pages/techExperienceCoursePage"),
);
const ReferPage = lazy(() => import("./pages/refer"));

const RouteFallback = () => <div className="min-h-screen bg-white" />;

const MyRoutes = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route exact path="/" element={<App />} />
          <Route exact path="/about" element={<About />} />
          <Route exact path="/courses/:slug/:type" element={<Courses />} />
          <Route exact path="/resources/faq" element={<FAQ />} />
          <Route exact path="/resources/blog" element={<BlogPage />} />
          <Route exact path="/resources/blog/:title" element={<BlogPost />} />
          <Route exact path="/resources/qa" element={<LiveQA />} />
          <Route exact path="/resources/refer" element={<ReferAFriend />} />
          <Route exact path="/all-course" element={<AllCoursePage />} />
          <Route exact path="/refer" element={<ReferPage />} />
          <Route
            exact
            path="/dashboard/recommended-courses"
            element={<RecommendedAllCoursePage />}
          />
          <Route exact path="/fast-track" element={<FastTrack />} />
          <Route
            exact
            path="/course-details/:id"
            element={<CourseDetailsPage />}
          />
          <Route
            exact
            path="/project-details/:id"
            element={<ProjectDetailsPage />}
          />
          <Route
            exact
            path="/course-details/:id/checkout"
            element={<CheckOutPage />}
          />
          <Route
            exact
            path="/project-details/:id/checkout"
            element={<CheckOutPage />}
          />
          <Route
            exact
            path="/terms-and-conditions"
            element={<TermsAndCondition />}
          />
          <Route exact path="/contact" element={<Contact />} />
          <Route exact path="/contact-partner" element={<ContactPartner />} />
          <Route exact path="/work-skills" element={<WorkSkillGaragePage />} />
          <Route exact path="/testimonies" element={<Testimonial />} />
          <Route exact path="/free-course/:type" element={<FreeCourse />} />
          <Route exact path="/short-course/:id" element={<ShortCoursePage />} />
          <Route exact path="/waitList" element={<WaitList />} />
          <Route exact path="/signup" element={<SignUpPage />} />
          <Route exact path="/mentorsignup" element={<MentorSignUpPage />} />
          <Route
            exact
            path="/email-verification-success"
            element={<EmailVerificationSuccess />}
          />
          <Route
            exact
            path="/reset-password-email-sent"
            element={<ResetPasswordEmailSent />}
          />
          <Route exact path="/newpassword" element={<NewPasswordPage />} />
          <Route
            exact
            path="/password-success"
            element={<PasswordSuccessPage />}
          />
          <Route exact path="/resend-email" element={<ResendEmailPage />} />
          <Route exact path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            exact
            path="/mentor-verify-email"
            element={<MentorVerifyEmailPage />}
          />
          <Route exact path="/reset-password" element={<ResetPasswordPage />} />
          <Route exact path="/login" element={<LoginPage />} />
          <Route
            exact
            path="/verification-code"
            element={<VerificationCodePage />}
          />
          <Route exact path="/expired-link" element={<ResetLinkPage />} />
          <Route exact path="/dashboard" element={<DashboardPage />} />
          <Route exact path="/all-mentors" element={<AllMentorsPage />} />
          <Route exact path="/bookings" element={<BookingsPage />} />
          <Route exact path="/profile" element={<Profile />} />
          <Route exact path="/mentor/:id" element={<MentorDetails />} />
          <Route exact path="/404" element={<PageNotFound />} />
          <Route exact path="/refund-policy" element={<RefundPolicyPage />} />
          <Route exact path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route exact path="/cookie-policy" element={<CookiePolicy />} />
          <Route exact path="/quiz" element={<QuizPage />} />
          <Route
            exact
            path="/self-paced/:id"
            element={
              
                <SelfPacedCourse />
            }
          />
          <Route
            exact
            path="/speak-to-advisor"
            element={<SpeakToAdvisorPage />}
          />
          <Route exact path="/quiz-completed" element={<QuizCompletedPage />} />
          <Route exact path="/watch-video/:id" element={<WatchVideoPage />} />
          <Route
            exact
            path="/tech-experience-course"
            element={<TechExperienceCoursePage />}
          />
          <Route
            exact
            path="/course-landing/:id"
            element={
              <AuthProtectedRoute>
                <CourseLandingPage />
              </AuthProtectedRoute>
            }
          />
          <Route
            exact
            path="/verification-expired"
            element={<VerificationExpired />}
          />
          <Route exact path="/success-email" element={<SuccessEmail />} />
          <Route
            exact
            path="/mentor-profile/sessions"
            element={
              <MentorRouteGuard>
                <ProtectedRoute requireApproval={true}>
                  <MentorSessions />
                </ProtectedRoute>
              </MentorRouteGuard>
            }
          />
          <Route
            exact
            path="/mentor-profile/me"
            element={
              <MentorRouteGuard>
                <MentorProfile />
              </MentorRouteGuard>
            }
          />
          <Route exact path="/become-a-mentor" element={<BecomeAMentorPage />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default MyRoutes;
